import { c as _c } from "react/compiler-runtime";
import type { PropsWithStyles } from '@morpho-org/ui-v6/types';
import { ExternalLink } from '@morpho-org/ui-v6/server';
import { cn } from '@morpho-org/ui-v6/utils';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { ThemeSegmentedControl } from '@repo/ui';
import { type ForwardedRef, forwardRef } from 'react';

// Use the app's NavSegmentControl so the burger menu renders the same tone-aware
// inline count pills as the desktop nav / mobile sub-nav.
import { NavSegmentControl } from '@/components/common/NavSegmentControl';
import { BLOG_URL, CAREER_URL, DATA_URL, DOCUMENTATION_URL, FORUM_URL, HELP_CENTER_URL, LANDING_URL, MORPHO_PRIVACY_URL, MORPHO_TOU_URL, REWARDS_URL, X_URL } from '@/config/external-urls';
import { useIsLgUp } from '@/hooks/useBreakpoints';
import { useNavigationOptions } from '@/hooks/useNavigationOptions';
import { useTrackPortfolioNavTabViewed } from '@/lib/analytics/client/hooks/useTrackPortfolioNavTabViewed';
import AnalyticsSwitch from '../AnalyticsSwitch';
export interface MenuDropdownProps extends PropsWithStyles {
  trigger: React.ReactNode;
  rootProps?: React.ComponentProps<typeof DropdownMenu.Root>;
  triggerProps?: React.ComponentProps<typeof DropdownMenu.Trigger>;
  contentProps?: React.ComponentProps<typeof DropdownMenu.Content>;
  positionRelativeToViewport?: boolean;
  onOpenChange: (open: boolean) => void;
  isOpen: boolean;
}

/**
 * MenuDropdown component displays a dropdown menu with Menu items.
 *
 * @param {Object} props - The component props.
 * @param {React.ReactNode} props.trigger - The trigger element for the dropdown.
 * @param {React.ComponentProps<typeof DropdownMenu.Root>} [props.rootProps] - Props for the DropdownMenu.Root component.
 * @param {React.ComponentProps<typeof DropdownMenu.Trigger>} [props.triggerProps] - Props for the DropdownMenu.Trigger component.
 * @param {React.ComponentProps<typeof DropdownMenu.Content>} [props.contentProps] - Props for the dropdown content.
 * @param {boolean} [props.positionRelativeToViewport=true] - Whether to position the dropdown relative to the viewport.
 * @param {ForwardedRef<HTMLDivElement>} ref - Forwarded ref for the dropdown content.
 * @returns {JSX.Element} The MenuDropdown component.
 */
const MenuDropdown = (t0, ref) => {
  const $ = _c(52);
  const {
    trigger,
    rootProps,
    triggerProps,
    contentProps,
    positionRelativeToViewport: t1,
    isOpen,
    onOpenChange
  } = t0;
  const positionRelativeToViewport = t1 === undefined ? true : t1;
  const {
    navigationValue,
    navigationOptions,
    onNavigationValueChange
  } = useNavigationOptions();
  const isLgUp = useIsLgUp();
  useTrackPortfolioNavTabViewed(isOpen && !isLgUp);
  let t2;
  if ($[0] !== onNavigationValueChange) {
    t2 = value => {
      onNavigationValueChange(value);
    };
    $[0] = onNavigationValueChange;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const onNavigationChange = t2;
  let t3;
  if ($[2] !== contentProps) {
    t3 = contentProps ?? {};
    $[2] = contentProps;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let contentClassName;
  let contentPropsRest;
  if ($[4] !== t3) {
    ({
      className: contentClassName,
      ...contentPropsRest
    } = t3);
    $[4] = t3;
    $[5] = contentClassName;
    $[6] = contentPropsRest;
  } else {
    contentClassName = $[5];
    contentPropsRest = $[6];
  }
  const t4 = positionRelativeToViewport && "left-md fixed top-[74px]";
  let t5;
  if ($[7] !== contentClassName || $[8] !== t4) {
    t5 = cn("text-foreground bg-popover rounded-[20px] border-[0.5px] border-border shadow-lg z-60", "animate-fade-in-fast focus:outline-none", "p-sm flex flex-col gap-[18px]", "w-auto min-w-[176px] max-lg:w-[calc(100vw-40px)]", "max-h-[calc(100dvh-84px)] overflow-y-auto", t4, contentClassName);
    $[7] = contentClassName;
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== navigationOptions) {
    t6 = navigationOptions.slice(0, 5);
    $[10] = navigationOptions;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== onNavigationChange || $[13] !== onOpenChange) {
    t7 = value_0 => {
      onOpenChange(false);
      onNavigationChange(value_0 as string);
    };
    $[12] = onNavigationChange;
    $[13] = onOpenChange;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== navigationValue || $[16] !== t6 || $[17] !== t7) {
    t8 = <NavSegmentControl variant="ghost" size="default" orientation="vertical" id="navbar-segment-control-mobile" options={t6} onChange={t7} value={navigationValue} className="gap-2xs hidden max-lg:flex" />;
    $[15] = navigationValue;
    $[16] = t6;
    $[17] = t7;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <div aria-hidden={true} className="border-border hidden border-t-[0.5px] max-lg:block" />;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <span className="text-foreground ml-[6px] flex h-[25px] items-center text-xs">Company</span>;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  let t11;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <ExternalLink href={LANDING_URL} className="group hover:bg-muted flex h-[25px] w-full min-w-[176px] items-center rounded-lg px-[6px]"><span className="text-muted-foreground group-hover:text-foreground text-xs">Morpho.org</span></ExternalLink>;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  let t12;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <ExternalLink href={CAREER_URL} className="group hover:bg-muted flex h-[25px] w-full min-w-[176px] items-center rounded-lg px-[6px]"><span className="text-muted-foreground group-hover:text-foreground text-xs">Jobs</span></ExternalLink>;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  let t13;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <ExternalLink href={BLOG_URL} className="group hover:bg-muted flex h-[25px] w-full min-w-[176px] items-center rounded-lg px-[6px]"><span className="text-muted-foreground group-hover:text-foreground text-xs">Blog</span></ExternalLink>;
    $[23] = t13;
  } else {
    t13 = $[23];
  }
  let t14;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <ExternalLink href={X_URL} className="group hover:bg-muted flex h-[25px] w-full min-w-[176px] items-center rounded-lg px-[6px]"><span className="text-muted-foreground group-hover:text-foreground text-xs">X</span></ExternalLink>;
    $[24] = t14;
  } else {
    t14 = $[24];
  }
  let t15;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <div className="flex w-full flex-col items-start">{t10}{t11}{t12}{t13}{t14}<ExternalLink href={HELP_CENTER_URL} className="group hover:bg-muted flex h-[25px] w-full min-w-[176px] items-center rounded-lg px-[6px]"><span className="text-muted-foreground group-hover:text-foreground text-xs">Help</span></ExternalLink></div>;
    $[25] = t15;
  } else {
    t15 = $[25];
  }
  let t16;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <span className="text-foreground ml-[6px] flex h-[25px] items-center text-xs">Protocol</span>;
    $[26] = t16;
  } else {
    t16 = $[26];
  }
  let t17;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <ExternalLink href={DOCUMENTATION_URL} className="group hover:bg-muted flex h-[25px] w-full min-w-[176px] items-center rounded-lg px-[6px]"><span className="text-muted-foreground group-hover:text-foreground text-xs">Documentation</span></ExternalLink>;
    $[27] = t17;
  } else {
    t17 = $[27];
  }
  let t18;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = <ExternalLink href={DATA_URL} className="group hover:bg-muted flex h-[25px] w-full min-w-[176px] items-center rounded-lg px-[6px]"><span className="text-muted-foreground group-hover:text-foreground text-xs">Analytics</span></ExternalLink>;
    $[28] = t18;
  } else {
    t18 = $[28];
  }
  let t19;
  if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = <ExternalLink href={REWARDS_URL} className="group hover:bg-muted flex h-[25px] w-full min-w-[176px] items-center rounded-lg px-[6px]"><span className="text-muted-foreground group-hover:text-foreground text-xs">Rewards</span></ExternalLink>;
    $[29] = t19;
  } else {
    t19 = $[29];
  }
  let t20;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = <ExternalLink href={FORUM_URL} className="group hover:bg-muted flex h-[25px] w-full min-w-[176px] items-center rounded-lg px-[6px]"><span className="text-muted-foreground group-hover:text-foreground text-xs">Forum</span></ExternalLink>;
    $[30] = t20;
  } else {
    t20 = $[30];
  }
  let t21;
  if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = <ExternalLink href={MORPHO_TOU_URL} className="group hover:bg-muted flex h-[25px] w-full min-w-[176px] items-center rounded-lg px-[6px]"><span className="text-muted-foreground group-hover:text-foreground text-xs">Terms of Use</span></ExternalLink>;
    $[31] = t21;
  } else {
    t21 = $[31];
  }
  let t22;
  if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = <div className="gap-sm flex flex-col items-start">{t15}<div className="flex w-full flex-col">{t16}{t17}{t18}{t19}{t20}{t21}<ExternalLink href={MORPHO_PRIVACY_URL} className="group hover:bg-muted flex h-[25px] w-full min-w-[176px] items-center rounded-lg px-[6px]"><span className="text-muted-foreground group-hover:text-foreground text-xs">Privacy Policy</span></ExternalLink></div><AnalyticsSwitch /><ThemeSegmentedControl className="w-full" /></div>;
    $[32] = t22;
  } else {
    t22 = $[32];
  }
  let t23;
  if ($[33] !== contentPropsRest || $[34] !== ref || $[35] !== t5 || $[36] !== t8) {
    t23 = <DropdownMenu.Content ref={ref} className={t5} {...contentPropsRest}>{t8}{t9}{t22}</DropdownMenu.Content>;
    $[33] = contentPropsRest;
    $[34] = ref;
    $[35] = t5;
    $[36] = t8;
    $[37] = t23;
  } else {
    t23 = $[37];
  }
  const content = t23;
  let t24;
  if ($[38] !== content || $[39] !== positionRelativeToViewport) {
    t24 = positionRelativeToViewport ? <div className="[&_[data-radix-popper-content-wrapper]]:!relative [&_[data-radix-popper-content-wrapper]]:!transform-none [&_[data-radix-popper-content-wrapper]]:!will-change-auto">{content}</div> : content;
    $[38] = content;
    $[39] = positionRelativeToViewport;
    $[40] = t24;
  } else {
    t24 = $[40];
  }
  const wrappedContent = t24;
  let t25;
  if ($[41] !== trigger || $[42] !== triggerProps) {
    t25 = <DropdownMenu.Trigger asChild={true} {...triggerProps}>{trigger}</DropdownMenu.Trigger>;
    $[41] = trigger;
    $[42] = triggerProps;
    $[43] = t25;
  } else {
    t25 = $[43];
  }
  let t26;
  if ($[44] !== wrappedContent) {
    t26 = <DropdownMenu.Portal>{wrappedContent}</DropdownMenu.Portal>;
    $[44] = wrappedContent;
    $[45] = t26;
  } else {
    t26 = $[45];
  }
  let t27;
  if ($[46] !== isOpen || $[47] !== onOpenChange || $[48] !== rootProps || $[49] !== t25 || $[50] !== t26) {
    t27 = <DropdownMenu.Root {...rootProps} modal={false} onOpenChange={onOpenChange} open={isOpen}>{t25}{t26}</DropdownMenu.Root>;
    $[46] = isOpen;
    $[47] = onOpenChange;
    $[48] = rootProps;
    $[49] = t25;
    $[50] = t26;
    $[51] = t27;
  } else {
    t27 = $[51];
  }
  return t27;
};
export default forwardRef(MenuDropdown);